import { omit } from 'lodash';
import { Overwrite, ValuesType } from 'utility-types';

import { DateRange } from '@app/utils';
import { sortByItemsMessages } from '@app/translations';
import { SORT_DIRECTION, SortOption } from '@app/constants';

export const FACET = {
  STATUS: 'patronItemStatus',
  FORMAT: 'sourceTypes',
  LOCATION: 'items.effectiveLocationId',
  PUBLISHER: 'instancePublishers',
  LANGUAGE: 'languages',
  SUBJECT: 'instanceSubjects',
  PUBLICATION_YEAR: 'publicationYear',
} as const;

export const FACETS_ORDER = Object.values(omit(FACET, 'PUBLICATION_YEAR'));
export type FacetKeys = ValuesType<typeof FACETS_ORDER>;

export enum OPERATOR {
  ALL = 'all',
  ANY = 'any',
  EQUAL = '=',
  PHRASE_MATCH = '==',
  GREATER_THAN = '>',
  LESS_THAN = '<',
}

export const ALL_RECORDS_CQL = 'cql.allRecords=1';

export const PUBLICATION_YEAR_FIELDS_MAP = {
  allRecords: ALL_RECORDS_CQL,
  publicationYearMin: `${FACET.PUBLICATION_YEAR}${OPERATOR.GREATER_THAN}`,
  publicationYearMax: `${FACET.PUBLICATION_YEAR}${OPERATOR.LESS_THAN}`,
};

export enum SEARCH_OPTION {
  keyword = 'keyword',
  title = 'title',
  author = 'author',
  issn = 'issn',
  isbn = 'isbn',
  lccn = 'lccn',
  subject = 'subject',
  advanced = 'advanced',
  query = 'query',
}

export const SEARCH_OPTIONS = [
  SEARCH_OPTION.keyword,
  SEARCH_OPTION.title,
  SEARCH_OPTION.author,
  SEARCH_OPTION.issn,
  SEARCH_OPTION.isbn,
  SEARCH_OPTION.lccn,
  SEARCH_OPTION.subject,
  SEARCH_OPTION.advanced,
  SEARCH_OPTION.query,
];

export const searchOptionMap = {
  [SEARCH_OPTION.keyword]: `keyword ${OPERATOR.ALL}`,
  [SEARCH_OPTION.title]: `title ${OPERATOR.ALL}`,
  [SEARCH_OPTION.author]: `contributors ${OPERATOR.ALL}`,
  [SEARCH_OPTION.issn]: `issn ${OPERATOR.EQUAL}`,
  [SEARCH_OPTION.isbn]: `isbn ${OPERATOR.EQUAL}`,
  [SEARCH_OPTION.lccn]: `lccn ${OPERATOR.EQUAL}`,
  [SEARCH_OPTION.subject]: `subjects ${OPERATOR.ALL}`,
};

export enum SORT_FIELD {
  relevance = 'relevance',
  title = 'title',
  author = 'author',
  publicationYear = 'publicationYear',
}

export const sortFieldMap = {
  [SORT_FIELD.title]: 'title',
  [SORT_FIELD.author]: 'contributors',
  [SORT_FIELD.publicationYear]: 'publicationYear',
};

export const facetToOperatorMap = {
  [FACET.STATUS]: OPERATOR.PHRASE_MATCH,
  [FACET.LOCATION]: OPERATOR.PHRASE_MATCH,
  [FACET.FORMAT]: OPERATOR.PHRASE_MATCH,
  [FACET.LANGUAGE]: OPERATOR.PHRASE_MATCH,
  [FACET.PUBLISHER]: OPERATOR.PHRASE_MATCH,
  [FACET.SUBJECT]: OPERATOR.PHRASE_MATCH,
};

export const IDENTIFIER_TYPE = {
  ISBN: 'ISBN',
  ISSN: 'ISSN',
  LCCN: 'LCCN',
  OCLC: 'OCLC',
};

export enum CLASSIFICATION_TYPE {
  lc = 'LC',
  dewey = 'Dewey',
}

export const searchResultSortItems: SortOption[] = [
  {
    label: sortByItemsMessages.relevance,
    sortField: SORT_FIELD.relevance,
  },
  {
    label: sortByItemsMessages.title,
    sortField: SORT_FIELD.title,
    sortDirection: SORT_DIRECTION.asc,
  },
  {
    label: sortByItemsMessages.title,
    sortField: SORT_FIELD.title,
    sortDirection: SORT_DIRECTION.desc,
  },
  {
    label: sortByItemsMessages.author,
    sortField: SORT_FIELD.author,
    sortDirection: SORT_DIRECTION.asc,
  },
  {
    label: sortByItemsMessages.author,
    sortField: SORT_FIELD.author,
    sortDirection: SORT_DIRECTION.desc,
  },
  {
    label: sortByItemsMessages.publicationYear,
    sortField: SORT_FIELD.publicationYear,
    sortDirection: SORT_DIRECTION.asc,
  },
  {
    label: sortByItemsMessages.publicationYear,
    sortField: SORT_FIELD.publicationYear,
    sortDirection: SORT_DIRECTION.desc,
  },
];

export const SEARCH_HISTORY_KEY = 'searchHistory';
export const SAVED_INSTANCES_KEY = 'savedInstances';
export const SAVED_SEARCHES_KEY = 'savedSearches';
export const VIEWED_RECORDS_KEY = 'viewedRecords';

export interface SearchResultsDto {
  totalRecords: number;
  instances?: SearchInstanceDto[];
}

export interface SearchResultsErrorsDto {
  totalRecords: number;
  errors: SearchInstanceErrorDto[];
}

export interface ItemDto {
  effectiveShelvingOrder: string;
}

export interface ElectronicAccessDto {
  uri?: string;
  linkText?: string;
  materialsSpecification?: string;
  publicNote?: string;
}

export type ElectronicAccessDtoWithURI = Overwrite<
  ElectronicAccessDto,
  { uri: string }
>;

export interface SearchInstanceErrorDto {
  code: string;
  message: string;
  type: string;
}

export interface ParentInstancesDto {
  superInstanceId: string;
}

export interface ChildInstancesDto {
  subInstanceId: string;
}

export type SearchInstanceSubjectsDto =
  | string[]
  | {
      value: string;
    }[];

export interface ContributorDto {
  name: string;
  primary?: boolean;
  contributorTypeName?: string;
  contributorTypeText?: string;
}

export interface PublicationDto {
  dateOfPublication: string;
  place?: string;
  publisher?: string;
}

export type LccnPermalinksSet = {
  marcxml?: string;
  mods?: string;
  bibframe2?: string;
  dc?: string;
};

export interface SearchInstanceDto {
  id: string;
  title: string;
  publication: PublicationDto[];
  parentInstances?: ParentInstancesDto[];
  childInstances?: ChildInstancesDto[];
  source?: string;
  languages?: string[];
  editions?: string[];
  subjects?: SearchInstanceSubjectsDto;
  series?: { value: string }[];
  sourceTypes?: string[];
  contributors?: ContributorDto[];
  alternativeTitles?: { alternativeTitle: string }[];
  precedingTitles?: {
    title: string;
    precedingInstanceId?: string;
  }[];
  succeedingTitles?: {
    title: string;
    succeedingInstanceId?: string;
  }[];
  items?: ItemDto[];
  notes?: { labelKey: string; note: string[] }[];
  electronicAccess?: ElectronicAccessDto[];
  isbns?: string[];
  issns?: string[];
  lccns?: string[];
  oclcs?: string[];
  lccnPermalinks?: LccnPermalinksSet[];
  effectiveShelvingOrder?: string;
  physicalDescriptions?: string[];
  classifications?: {
    classificationNumber: string;
    classificationTypeName: string;
  }[];
}

interface LibraryLocationDto {
  name: string;
  code?: string;
}

export interface HoldingAvailability {
  id: string;
  callNumber: string;
  location: LibraryLocationDto;
  status: HoldingAvailabilityStatus;
  holdingsStatements: { statement: string }[];
  library?: LibraryLocationDto;
  dueDate?: string;
  volume?: string;
}

export interface HoldingsAvailabilityDto {
  instanceId: string;
  requestable: boolean;
  isRTACApplicable: boolean;
  hasItems?: boolean;
  holdings: HoldingAvailability[];
}

export interface SearchState {
  option: SEARCH_OPTION;
  query?: string;
  pageNumber?: number;
  sortField?: SORT_FIELD;
  sortDirection?: SORT_DIRECTION;
  facets?: SelectedFacets;
  isInnerUpdate?: boolean;
  publicationYear?: DateRange;
  isNewPageRequest?: boolean;
  recordsPerPage?: number;
  limit?: number;
  offset?: number;
}

export type SelectedFacets = {
  [propName in (typeof FACETS_ORDER)[0]]?: string[];
};

export type SelectedFacetAttributes = Partial<
  Record<FacetKeys, Record<string, boolean>>
>;

export interface HoldingAvailabilityStatus {
  generalStatus: RecordAvailabilityGeneralStatus;
  customIcon: RecordAvailabilityIconColor;
  customDisplayStatus: string;
}

export enum RecordAvailabilityIconColor {
  red = 'red',
  green = 'green',
  yellow = 'yellow',
  error = 'error',
}

export enum RecordAvailabilityGeneralStatus {
  available = 'Available',
  notAvailable = 'Not Available',
  error = 'Error',
  unknown = 'Unknown',
}
